export interface IndustryPainPoint {
  pain: string;
  impact: string;
  solution: string;
}

export interface IndustryStackGroup {
  category: string;
  tools: string[];
}

export interface IndustryDeliverableGroup {
  category: string;
  items: string[];
}

export interface IndustryProof {
  quote: string;
  author: string;
  stage?: string;
  plan?: string;
}

export interface IndustryPricingTier {
  name: string;
  price: string;
  bestFor: string;
  features: string[];
  popular?: boolean;
}

export interface IndustryDetail {
  slug: string;
  title: string;
  metaTitle: string;
  metaDescription: string;
  description: string;
  heroStat: { value: string; label: string };
  painPoints: IndustryPainPoint[];
  stack: IndustryStackGroup[];
  deliverables: IndustryDeliverableGroup[];
  proof: IndustryProof;
  pricingTiers: IndustryPricingTier[];
  ctaText: string;
  ctaHref: string;
}

export const industryDetails: Record<string, IndustryDetail> = {
  saas: {
    slug: "saas",
    title: "Accounting & Finance Operations for SaaS & Tech Startups",
    metaTitle: "SaaS Bookkeeping & ASC 606 Revenue Recognition | Finendra",
    metaDescription:
      "Subscription rev rec, deferred revenue waterfalls, Stripe reconciliation, and board-ready SaaS metrics (CAC, LTV, NRR, Magic Number) for seed to Series A software companies.",
    description:
      "From Stripe billing sync to complex ASC 606 ratable revenue recognition, we build audit-ready books and board decks that give founders complete visibility over churn, burn, and net runway.",
    heroStat: { value: "5th", label: "Monthly Close & Board Pack Delivery" },
    painPoints: [
      {
        pain: "Unearned / Deferred Revenue Confusion",
        impact:
          "Overstating revenue on tax returns or misleading investors on true MRR vs. cash collections.",
        solution:
          "Automated ASC 606 deferred revenue waterfall schedules amortized ratably over subscription terms.",
      },
      {
        pain: "Messy Stripe & Merchant Gateway Syncs",
        impact:
          "Hundreds of unlinked payment processor fees, refunds, chargebacks, and payouts creating reconciliation chaos.",
        solution:
          "Clean clearing-account workflows matching gross billings, refunds, and merchant fees to the exact penny.",
      },
      {
        pain: "Investor Due Diligence Friction",
        impact:
          "Fundraises delayed weeks while accountants scramble to clean up cash-basis records into GAAP accrual workpapers.",
        solution:
          "Day-one GAAP compliance, CPA-ready balance sheets, and cap table / 83(b) tracking so audits take 48 hours.",
      },
      {
        pain: "Economic Nexus & Digital Sales Tax Exposure",
        impact:
          "Unintended tax liabilities in states like NY, CA, TX, and WA once digital subscription thresholds are crossed.",
        solution:
          "Quarterly sales tax nexus monitoring and automated filings via TaxJar and Avalara integrations.",
      },
    ],
    stack: [
      { category: "Core Accounting", tools: ["QuickBooks Online", "Xero", "NetSuite"] },
      {
        category: "Billing & Revenue",
        tools: ["Stripe", "Chargebee", "Maxio (SaaSOptics)", "Paddle"],
      },
      { category: "Spend & Cards", tools: ["Brex", "Ramp", "Mercury", "Bill.com"] },
      { category: "Payroll & Equity", tools: ["Gusto", "Rippling", "Carta"] },
    ],
    deliverables: [
      {
        category: "Monthly Close (By the 5th)",
        items: [
          "Accrual-basis P&L, Balance Sheet, and Cash Flow statement",
          "Deferred revenue waterfall schedule with contract-level rev rec",
          "Stripe/Chargebee gross-to-net billing and refund reconciliation",
          "Departmental P&L segmentation (Engineering, Sales, Marketing, G&A)",
        ],
      },
      {
        category: "Investor & Board Metrics",
        items: [
          "Monthly board pack: ARR, MRR, gross churn, net retention (NRR)",
          "Unit economics analysis: CAC, LTV, CAC payback period, Magic Number",
          "Net burn rate and runway in months forecasting with hiring scenario models",
        ],
      },
      {
        category: "Tax & Diligence Support",
        items: [
          "Multi-state sales tax economic nexus tracking (SaaS taxability rules)",
          "Year-end tax workpapers and R&D tax credit payroll offset support",
          "Virtual data room preparation for Seed and Series A financing rounds",
        ],
      },
    ],
    proof: {
      quote:
        "Set up our books day 1 post-incorporation. When we raised seed, due diligence took 2 days — clean as a whistle. Finendra's ASC 606 revenue waterfall saved our round.",
      author: "Founder, YC W24 SaaS ($4M ARR)",
      stage: "Pre-seed → Seed",
      plan: "Starter → Growth",
    },
    pricingTiers: [
      {
        name: "Starter",
        price: "$499/mo",
        bestFor: "Pre-revenue to $50K MRR • Cash basis OK",
        features: [
          "Monthly bookkeeping & bank recs",
          "Quarterly P&L + balance sheet",
          "QBO setup & chart of accounts",
        ],
      },
      {
        name: "Growth",
        price: "$1,299/mo",
        bestFor: "$50K–$200K MRR • Seed-stage SaaS",
        popular: true,
        features: [
          "Accrual accounting + ASC 606 rev rec",
          "Monthly close by 5th + board pack",
          "Burn rate, runway, and cash forecast",
          "Gusto/Rippling payroll & AP/AR",
        ],
      },
      {
        name: "Scale",
        price: "$2,499/mo",
        bestFor: "$200K–$500K MRR • Series A Prep",
        features: [
          "Multi-entity consolidation",
          "Deferred revenue waterfall by contract",
          "Departmental P&L + Big 4 audit readiness",
          "Sales tax filing across 15+ states",
        ],
      },
    ],
    ctaText: "Book SaaS Financial Diagnostic",
    ctaHref: "/contact?industry=saas",
  },

  ecommerce: {
    slug: "ecommerce",
    title: "E-commerce & DTC Accounting: Inventory, COGS & Sales Tax",
    metaTitle: "E-commerce Accounting & DTC Bookkeeping | Finendra",
    metaDescription:
      "Multi-channel reconciliation (Shopify, Amazon, TikTok Shop), automated COGS, landed cost allocations, and multi-state sales tax nexus tracking for 7- and 8-figure brands.",
    description:
      "Stop guessing your true gross margin. We tie inventory valuations, multi-channel marketplace payouts, and landed freight costs into crystal-clear monthly financial statements.",
    heroStat: { value: "99.9%", label: "Inventory & COGS Accuracy" },
    painPoints: [
      {
        pain: "Multi-Channel Reconciliation Chaos",
        impact:
          "Shopify, Amazon Seller Central, Walmart, and TikTok payouts lumped together with opaque fee deductions.",
        solution:
          "A2X and custom clearing rules allocating gross sales, marketplace seller fees, promo discounts, and returns accurately.",
      },
      {
        pain: "Distorted COGS & Landed Costs",
        impact:
          "Expensing inventory upon purchase rather than sale, producing massive artificial P&L swings month-over-month.",
        solution:
          "Accrual inventory accounting with FIFO landed cost capitalization (ocean freight, customs, drayage, 3PL prep).",
      },
      {
        pain: "Multi-State Sales Tax Liability",
        impact:
          "Crossing economic nexus thresholds in 20+ states without filing, creating audit and penalty traps.",
        solution:
          "Automated nexus monitoring, TaxJar/Avalara configuration, and state-by-state return preparation.",
      },
      {
        pain: "Cash Flow & Reorder Blindspots",
        impact:
          "Stockouts during peak seasonality or running out of operational cash to fund production POs.",
        solution:
          "13-week rolling cash flow forecasts modeling purchase orders, supplier terms, and working capital cycles.",
      },
    ],
    stack: [
      {
        category: "E-Commerce Platforms",
        tools: ["Shopify Plus", "Amazon Seller Central", "TikTok Shop", "Walmart"],
      },
      { category: "Sync & Inventory", tools: ["A2X", "Cin7", "Katana", "Finale Inventory"] },
      { category: "Tax & Compliance", tools: ["TaxJar", "Avalara", "Anrok"] },
      { category: "Accounting & Spend", tools: ["QuickBooks Online", "Ramp", "Bill.com", "Gusto"] },
    ],
    deliverables: [
      {
        category: "Channel-Level P&L (Monthly)",
        items: [
          "Gross-to-net sales waterfall by marketplace (Shopify, Amazon, Wholesale)",
          "True contribution margin after marketing spend (MER/ROAS) and shipping costs",
          "Automated return, discount, and chargeback reserves",
        ],
      },
      {
        category: "Inventory & Cost Accounting",
        items: [
          "FIFO landed cost allocation across SKUs (freight, duties, storage)",
          "Monthly inventory balance sheet true-up matching physical 3PL count",
          "Dead stock and slow-moving inventory reserve review",
        ],
      },
      {
        category: "Tax & Cash Management",
        items: [
          "State economic nexus threshold monitoring across all 50 states",
          "Sales tax filing prep and marketplace facilitator exemption auditing",
          "13-week cash runway and supplier PO financing modeling",
        ],
      },
    ],
    proof: {
      quote:
        "Bootstrapped to $1.2M GMV. Finendra handles our Shopify COGS, sales tax in 12 states, and monthly close — cheaper than a junior hire.",
      author: "Founder, DTC Home Goods ($1.2M GMV)",
      stage: "Bootstrapped",
      plan: "Growth",
    },
    pricingTiers: [
      {
        name: "Starter",
        price: "$499/mo",
        bestFor: "Under $50K/mo GMV • Single channel",
        features: [
          "Shopify or Amazon sync",
          "Monthly reconciliation & P&L",
          "Basic sales tax tracking",
        ],
      },
      {
        name: "Growth",
        price: "$1,299/mo",
        bestFor: "$50K–$200K/mo GMV • Multi-channel",
        popular: true,
        features: [
          "Multi-channel A2X setup",
          "Accrual COGS & landed cost allocation",
          "Sales tax nexus tracking (5 states)",
          "13-week cash flow forecast",
        ],
      },
      {
        name: "Scale",
        price: "$2,499/mo",
        bestFor: "$200K–$500K+/mo GMV • Omnichannel",
        features: [
          "Full 3PL/ERP inventory integration",
          "15+ state sales tax filings",
          "Channel-level contribution margins",
          "Quarterly CFO working capital review",
        ],
      },
    ],
    ctaText: "Book E-Commerce Diagnostic",
    ctaHref: "/contact?industry=ecommerce",
  },

  nonprofits: {
    slug: "nonprofits",
    title: "Fund Accounting & Grant Compliance for Nonprofits",
    metaTitle: "Nonprofit Bookkeeping & Single Audit Preparation | Finendra",
    metaDescription:
      "Fund accounting, federal grant budget tracking, restricted funds management, Statement of Functional Expenses, and Uniform Guidance Single Audit workpapers.",
    description:
      "Deliver rigorous fiscal transparency to donors and grantmakers. We handle fund accounting, restricted vs. unrestricted balances, and grant drawdowns so your audits return zero findings.",
    heroStat: { value: "0", label: "Findings on Single Audits Supported" },
    painPoints: [
      {
        pain: "Restricted vs. Unrestricted Fund Confusion",
        impact:
          "Accidentally spending donor-restricted grant funds on general overhead, risking grant clawbacks.",
        solution:
          "Class and tag-based fund accounting segregating time-restricted, purpose-restricted, and operational cash.",
      },
      {
        pain: "Federal Grant Drawdown Discrepancies",
        impact:
          "Delayed reimbursement from federal agencies (NIH, NSF, USAID) due to unsupported expense allocations.",
        solution:
          "Grant expenditure calendars and automated drawdown schedules reconciled to general ledger accounts.",
      },
      {
        pain: "Single Audit Stress & Penalty Risk",
        impact:
          "Disorganized records causing material findings under Uniform Guidance (2 CFR 200) audit reviews.",
        solution: "Audit-ready workpaper packages matching grant compliance criteria from day one.",
      },
      {
        pain: "Form 990 Preparation Bottlenecks",
        impact:
          "CPAs charging $10,000+ for annual 990 prep because functional expense allocations weren't tracked.",
        solution:
          "Continuous Statement of Functional Expenses categorizing spend across Program, Management, and Fundraising.",
      },
    ],
    stack: [
      {
        category: "Fund Accounting",
        tools: ["QuickBooks Online Nonprofit", "Xero", "Sage Intacct"],
      },
      { category: "Donations & Grants", tools: ["Bloomerang", "DonorPerfect", "Submittable"] },
      { category: "Spend & Payroll", tools: ["Bill.com", "Gusto", "Expensify", "Ramp"] },
    ],
    deliverables: [
      {
        category: "Fund Accounting & Statements",
        items: [
          "Statement of Financial Position and Statement of Activities",
          "Statement of Functional Expenses (Program vs. Admin vs. Fundraising)",
          "Restricted fund release schedules aligned with donor stipulations",
        ],
      },
      {
        category: "Grant & Compliance Operations",
        items: [
          "Grant-by-grant budget vs. actual variance reports",
          "Drawdown documentation support for government and foundation grants",
          "Time-and-effort tracking allocations for grant-funded personnel",
        ],
      },
      {
        category: "Board & Audit Readiness",
        items: [
          "Monthly finance committee and board reporting packages",
          "Single Audit (Uniform Guidance) workpaper binder ready for external auditors",
          "Form 990 schedule preparation support for tax filing CPAs",
        ],
      },
    ],
    proof: {
      quote:
        "12 federal grants, zero findings on Single Audit. Finendra built a grant calendar that made audit prep trivial. Our board has never had clearer numbers.",
      author: "CFO, Research Nonprofit ($6M Revenue)",
      stage: "$6M Revenue",
      plan: "Scale",
    },
    pricingTiers: [
      {
        name: "Starter",
        price: "$499/mo",
        bestFor: "Under $500K annual budget • 1–2 programs",
        features: [
          "Fund accounting setup",
          "Monthly reconciliation",
          "Form 990 preparation support",
        ],
      },
      {
        name: "Growth",
        price: "$1,299/mo",
        bestFor: "$500K–$2M annual budget • Multi-grant",
        popular: true,
        features: [
          "Grant budget vs. actual tracking",
          "Restricted fund release schedules",
          "Statement of Functional Expenses",
          "Monthly board financial deck",
        ],
      },
      {
        name: "Scale",
        price: "$2,499/mo",
        bestFor: "$2M–$10M budget • Federal Single Audit",
        features: [
          "Uniform Guidance audit workpapers",
          "Grant drawdown filing support",
          "Controller review + audit liaison",
          "Time & effort payroll allocation",
        ],
      },
    ],
    ctaText: "Discuss Nonprofit Finance Support",
    ctaHref: "/contact?industry=nonprofits",
  },

  "professional-services": {
    slug: "professional-services",
    title: "Accounting for Agencies, Consultancies & Professional Services",
    metaTitle: "Agency Bookkeeping & Project Profitability Accounting | Finendra",
    metaDescription:
      "Real-time client retainer tracking, billable utilization, pass-through expense reconciliation, multi-state contractor 1099 compliance, and agency cash flow forecasting.",
    description:
      "Stop leaking margin on fixed-fee client engagements. We track billable utilization, client retainers, contractor 1099s, and pass-through software expenses so you know which accounts drive real profit.",
    heroStat: { value: "40%", label: "Average Margin Improvement Visibility" },
    painPoints: [
      {
        pain: "Retainer Revenue & Deferred Billing",
        impact:
          "Treating client upfront deposits as immediate revenue before service hours are actually performed.",
        solution:
          "Proper deferred retainer accounting recognizing revenue earned against delivered milestones or hours.",
      },
      {
        pain: "Blindness on Client Project Profitability",
        impact:
          "High-revenue clients actually generating negative margins due to scope creep and contractor overruns.",
        solution:
          "Job costing matching direct contractor/employee labor and pass-through software to specific clients.",
      },
      {
        pain: "1099 Contractor Compliance Scramble",
        impact:
          "January panic locating missing W-9 forms and reporting multi-state independent contractor compensations.",
        solution:
          "Onboarding W-9 collection workflow, monthly contractor ledger audit, and automated 1099-NEC filings.",
      },
      {
        pain: "Lumpy Client Retainer Cash Flows",
        impact:
          "Clients paying Net 60 while your payroll and contractor bills hit on the 1st and 15th.",
        solution: "Disciplined AR aging follow-ups and 13-week cash conversion cycle forecasts.",
      },
    ],
    stack: [
      { category: "Accounting & Spend", tools: ["QuickBooks Online", "Xero", "Bill.com", "Ramp"] },
      { category: "Time & Project", tools: ["Harvest", "Clockify", "Toggl", "Asana"] },
      { category: "Payroll & Contractors", tools: ["Gusto", "Rippling", "Deel"] },
    ],
    deliverables: [
      {
        category: "Monthly Financials & Margins",
        items: [
          "Client-by-client gross margin and profitability analysis",
          "Billable team utilization metrics vs. non-billable overhead",
          "Monthly close by the 5th business day with accrual milestone adjustments",
        ],
      },
      {
        category: "Billing & Cash Acceleration",
        items: [
          "Automated recurring retainer invoicing and payment reconciliation",
          "AR aging collections management with polite automated follow-ups",
          "Client pass-through expense capture (media spend, software, travel)",
        ],
      },
      {
        category: "Contractor & Tax Compliance",
        items: [
          "W-9 collection on day one and year-end Form 1099-NEC filing",
          "Multi-state payroll tax registrations for remote consultants",
          "Cash runway forecasting modeling pipeline conversion scenarios",
        ],
      },
    ],
    proof: {
      quote:
        "We scaled our agency from 4 to 18 people. Finendra showed us which client retainers were actually profitable and fixed our payroll across 7 states. Worth 10x what we pay.",
      author: "Managing Director, Digital Product Studio ($2.5M Revenue)",
      stage: "Bootstrapped Scale",
      plan: "Growth",
    },
    pricingTiers: [
      {
        name: "Starter",
        price: "$499/mo",
        bestFor: "Solo or boutique agency • Up to $30K/mo revenue",
        features: [
          "Monthly bookkeeping & invoice matching",
          "Quarterly P&L + balance sheet",
          "Up to 5 contractors",
        ],
      },
      {
        name: "Growth",
        price: "$1,299/mo",
        bestFor: "$30K–$150K/mo revenue • Growing agency",
        popular: true,
        features: [
          "Client & project profitability tracking",
          "Monthly close by 5th + AP/AR follow-up",
          "Payroll (Gusto) & unlimited 1099 filings",
          "Retainer revenue recognition",
        ],
      },
      {
        name: "Scale",
        price: "$2,499/mo",
        bestFor: "$150K–$500K/mo revenue • Multi-team agency",
        features: [
          "Custom team utilization dashboards",
          "Departmental P&L & overhead allocation",
          "Cash flow forecast & scenario modeling",
          "Quarterly CFO margin review",
        ],
      },
    ],
    ctaText: "Book Agency Margin Diagnostic",
    ctaHref: "/contact?industry=professional-services",
  },

  "cross-border-india-us": {
    slug: "cross-border-india-us",
    title: "US-India Cross-Border Accounting & Compliance",
    metaTitle: "India→US Cross-Border Accounting, Transfer Pricing & Form 5472 | Finendra",
    metaDescription:
      "Accounting for Delaware C-Corps with Indian development subsidiaries: transfer pricing documentation, Form 5472 compliance, RBI/FEMA remittances, and dual-book consolidation.",
    description:
      "Scale your Delaware C-Corp parent while keeping your Indian engineering center 100% compliant. We align US GAAP with Indian MCA rules, manage transfer pricing markups, and handle foreign reporting.",
    heroStat: { value: "100%", label: "US & India Regulatory Alignment" },
    painPoints: [
      {
        pain: "Transfer Pricing & Cost-Plus Markups",
        impact:
          "IRS or Indian Income Tax Department auditing the intercompany service agreement due to non-arm's length pricing.",
        solution:
          "Documented cost-plus markup schedules (typically 10-15%) with monthly intercompany invoice reconciliation.",
      },
      {
        pain: "IRS Form 5472 & Foreign Disclosure Penalties",
        impact:
          "Severe $25,000 automatic IRS penalty per year for failure to report transactions with 25%+ foreign shareholders.",
        solution:
          "Year-round tracking of reportable intercompany loans, capital infusions, and service payments for Form 5472 prep.",
      },
      {
        pain: "Dual-Book Currency & Accounting Conflicts",
        impact:
          "US books in USD on US GAAP vs. Indian subsidiary books in INR on Ind AS / Schedule III creating confusion.",
        solution:
          "Dual-currency consolidation translating INR expenses to USD with proper FX gain/loss entries.",
      },
      {
        pain: "RBI, FEMA & 15CA/CB Remittance Hurdles",
        impact:
          "Funds stuck in transit between US Mercury/Brex accounts and Indian bank accounts due to missing CA certificates.",
        solution:
          "Complete documentation packs for Indian banks (Inward Remittance, FIRC, and 15CA/CB liaison support).",
      },
    ],
    stack: [
      { category: "US Entity", tools: ["QuickBooks Online", "Brex", "Mercury", "Gusto"] },
      { category: "India Entity", tools: ["Zoho Books", "Tally Prime", "RazorpayX", "HDFC/ICICI"] },
      { category: "Cross-Border FX", tools: ["Wise Business", "SVB / Mercury Wire", "Stripe"] },
    ],
    deliverables: [
      {
        category: "Consolidated Monthly Close",
        items: [
          "Consolidated monthly P&L in USD combining Delaware parent and India subsidiary",
          "Elimination of intercompany revenue and expense entries",
          "Foreign exchange (FX) realized and unrealized gain/loss calculations",
        ],
      },
      {
        category: "Transfer Pricing & Intercompany",
        items: [
          "Monthly cost-plus transfer pricing calculation and invoice generation",
          "Intercompany loan and equity contribution ledger tracking",
          "Documentation trail ready for Indian Transfer Pricing (TP) audit reports",
        ],
      },
      {
        category: "US & India Regulatory Support",
        items: [
          "Form 5472 / Form 1120 tax workpaper preparation for US CPA",
          "Indian MCA and GST monthly data reconciliation support",
          "FEMA/RBI compliance assistance for inward capital and vendor remittances",
        ],
      },
    ],
    proof: {
      quote:
        "As an Indian founder with a Delaware C-Corp and Bangalore dev center, transfer pricing was our nightmare. Finendra aligned both books seamlessly. When US VCs audited us, they found zero flaws.",
      author: "Founder, Cross-Border AI Platform ($3M Seed Raised)",
      stage: "Seed Stage",
      plan: "Growth",
    },
    pricingTiers: [
      {
        name: "Starter",
        price: "$499/mo",
        bestFor: "Pre-seed Delaware setup • 1 entity",
        features: [
          "US bookkeeping & bank recs",
          "EIN & US bank connection support",
          "Tax-ready annual workpapers",
        ],
      },
      {
        name: "Growth",
        price: "$1,299/mo",
        bestFor: "Delaware C-Corp + India Dev Team",
        popular: true,
        features: [
          "Dual-book US + India alignment",
          "Monthly transfer pricing calculation",
          "Form 5472 reportable transaction tracking",
          "USD consolidated monthly close",
        ],
      },
      {
        name: "Scale",
        price: "$2,499/mo",
        bestFor: "Funded Cross-Border Company",
        features: [
          "Multi-currency intercompany consolidation",
          "Transfer pricing study workpaper liaison",
          "RBI/FEMA remittance compliance pack",
          "Controller-level cross-border review",
        ],
      },
    ],
    ctaText: "Discuss Cross-Border Setup",
    ctaHref: "/contact?industry=cross-border-india-us",
  },

  startups: {
    slug: "startups",
    title: "Finance Operations for Startups: Pre-Seed to Series A",
    metaTitle: "Startup Bookkeeping & Accounting: Pre-Seed to Series A | Finendra",
    metaDescription:
      "Stage-based financial operations built for venture-backed and bootstrapped founders. Clean bookkeeping from day one, monthly close by the 5th, and investor-ready board packs.",
    description:
      "Don't let neglected books kill your next financing round. Finendra is bootstrapped ourselves — we price so early-stage founders can afford institutional-grade finance ops from day one.",
    heroStat: { value: "12+", label: "Venture-Backed & Bootstrapped Startups Supported" },
    painPoints: [
      {
        pain: "Founder Doing Bookkeeping at 2 AM",
        impact:
          "Valuable founder hours wasted categorizing transactions and fixing software sync bugs instead of building product.",
        solution:
          "Hands-off, dedicated finance operations. Hand over read-only access and never touch a ledger again.",
      },
      {
        pain: "Blindsided by Sudden Runway Drop",
        impact:
          "Relying on bank balance rather than accrued liabilities, discovering you have 3 months of cash instead of 7.",
        solution:
          "Net burn, gross burn, and zero-cash-date runway models updated on the 5th of every month.",
      },
      {
        pain: "Due Diligence Delays & Deal Friction",
        impact:
          "Lead investors asking for GAAP accrual statements and deferred revenue schedules during term sheet negotiations.",
        solution:
          "Day-one audit-ready workpapers, cap table consistency, and clean diligence binders.",
      },
      {
        pain: "Overpaying Enterprise Firms $3,000–$5,000/mo",
        impact:
          "Burning precious seed runway on high-overhead accounting firms that assign junior staff anyway.",
        solution:
          "Transparent, founder-friendly pricing starting at $199/mo with no long-term contracts.",
      },
    ],
    stack: [
      { category: "Banking & Cards", tools: ["Mercury", "Brex", "Ramp", "Silicon Valley Bank"] },
      { category: "Accounting", tools: ["QuickBooks Online", "Xero", "NetSuite"] },
      { category: "Payroll & HR", tools: ["Gusto", "Rippling", "Deel"] },
      { category: "Revenue & Equity", tools: ["Stripe", "Chargebee", "Carta", "Pulley"] },
    ],
    deliverables: [
      {
        category: "Pre-Seed ($0 - $50K MRR)",
        items: [
          "Cash-basis bookkeeping across bank and credit card accounts",
          "Monthly / Quarterly P&L, Balance Sheet, and tax-ready workpapers",
          "Founder personal vs. business expense hygiene separation",
          "83(b) election confirmation tracking and state tax setup",
        ],
      },
      {
        category: "Seed ($50K - $200K MRR)",
        items: [
          "Accrual accounting with ASC 606 revenue recognition basics",
          "Monthly close completed before the 5th business day",
          "Board-ready financial statement pack with burn and runway metrics",
          "Multi-state payroll execution via Gusto/Rippling and contractor 1099s",
        ],
      },
      {
        category: "Series A ($200K - $500K+ MRR)",
        items: [
          "Contract-level deferred revenue waterfall schedules",
          "Multi-entity consolidation (domestic and international subsidiaries)",
          "Departmental P&L segmentation and SaaS unit economics dashboard",
          "Big 4 audit readiness and virtual data room maintenance",
        ],
      },
    ],
    proof: {
      quote:
        "Set up our books day 1 post-incorporation. When we raised seed, due diligence took 2 days — clean as a whistle. There's no other team offering this level of precision for $199–$499/mo.",
      author: "Founder, YC W24 SaaS",
      stage: "Pre-seed → Seed",
      plan: "Essential → Growth",
    },
    pricingTiers: [
      {
        name: "Essential",
        price: "$199/mo",
        bestFor: "No full-time needed / Pre-seed ($0–$50K MRR)",
        features: [
          "Cash-basis bookkeeping & bank recs",
          "Monthly / Quarterly P&L + balance sheet",
          "Tax-ready books & 1099 tracking",
          "Up to 75 transactions/mo",
          "No contracts, cancel anytime",
        ],
      },
      {
        name: "Growth",
        price: "$499/mo",
        bestFor: "Seed Stage ($50K–$200K MRR)",
        popular: true,
        features: [
          "Accrual accounting & close by 5th",
          "Board pack, burn & cash runway forecast",
          "Gusto/Rippling payroll & AP/AR bill pay",
          "Multi-state sales tax nexus monitoring",
          "Dedicated accountant + monthly review",
        ],
      },
      {
        name: "Scale",
        price: "$1,299/mo",
        bestFor: "Series A / Scaling ($200K–$500K MRR)",
        features: [
          "Multi-entity consolidation",
          "Deferred revenue waterfall by contract",
          "Departmental P&L (Eng, Sales, Mktg)",
          "15+ state sales tax filings",
          "Big 4 audit workpaper prep",
        ],
      },
    ],
    ctaText: "Book Free Founder Diagnostic",
    ctaHref: "/contact?industry=startups",
  },
};
